// src/pages/admin/FilemakerSync.tsx
//
// Dedicated admin menu for FileMaker 19.2.2 sync status,
// data-quality control, anomaly triage.
//
// Layout: a header with the agent heartbeat + queue depth,
// then 11 layout cards (one per FileMaker layout from the
// Bench.jpg home screen). Each card shows:
//   - direction (FM -> AWS / AWS -> FM / bidirectional)
//   - last sync timestamp + delta
//   - total rows mirrored
//   - health (ok / warning / error)
//   - count of open anomalies + a "View" button to expand
//
// A dedicated section at the bottom lists every open anomaly
// with severity, summary, suggested fix, and a one-click
// "Mark reviewed" affordance (wired up to the store).

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Link, useParams } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  Database,
  Cloud,
  ArrowLeftRight,
  Eye,
  Check,
  ExternalLink,
  ChevronRight,
  RefreshCcw,
  AlertTriangle,
  Sparkles,
  ArrowUpRight,
  Shield,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import {
  FILEMAKER_LAYOUTS,
  FILEMAKER_LAYOUT_BY_KEY,
  ANOMALY_SEVERITY_TONE,
  type AnomalySeverity,
  type FilemakerLayoutKey,
  type SyncDirection,
} from "@/lib/filemaker-sync/types";
import { useSyncState, getOpenAnomalies, getAnomalyCounts } from "@/lib/filemaker-sync/useSyncState";
import {
  SEED_TASK_LOGS,
  SYNC_STATUS_TONE,
  type SyncTaskLog,
  type SyncTaskStatus,
} from "@/lib/filemaker-sync/taskLogs";

// ---------- shared ----------

/** i18n key per task-run status (machine value stays in the filter state). */
const TASK_STATUS_LABEL_KEY: Record<SyncTaskStatus, string> = {
  ok: "admin.filemakerSync.ok",
  warning: "admin.filemakerSync.warning",
  error: "admin.filemakerSync.error",
  running: "admin.filemakerSync.running",
};

/** i18n key per anomaly severity. */
const SEVERITY_LABEL_KEY: Record<AnomalySeverity, string> = {
  low: "admin.filemakerSync.severityLow",
  medium: "admin.filemakerSync.severityMedium",
  high: "admin.filemakerSync.severityHigh",
  critical: "admin.filemakerSync.severityCritical",
};

/** i18n key per sync health. */
const HEALTH_LABEL_KEY: Record<"ok" | "warning" | "error", string> = {
  ok: "admin.filemakerSync.ok",
  warning: "admin.filemakerSync.warning",
  error: "admin.filemakerSync.error",
};

function directionLabel(d: SyncDirection): {
  labelKey: string;
  Icon: LucideIcon;
  tone: string;
} {
  switch (d) {
    case "fm_to_aws":
      return {
        labelKey: "admin.filemakerSync.directionFmToAws",
        Icon: ArrowLeftRight,
        tone: "bg-benchmark-teal/15 text-benchmark-teal-bright",
      };
    case "aws_to_fm":
      return {
        labelKey: "admin.filemakerSync.directionAwsToFm",
        Icon: ArrowLeftRight,
        tone: "bg-accent-indigo/15 text-accent-indigo",
      };
    case "bidirectional":
      return {
        labelKey: "admin.filemakerSync.directionBidirectional",
        Icon: ArrowLeftRight,
        tone: "bg-accent-amber/15 text-accent-amber",
      };
    case "fm_local_readonly":
      return {
        labelKey: "admin.filemakerSync.directionReadOnly",
        Icon: Eye,
        tone: "bg-fg-quaternary/15 text-fg-tertiary",
      };
  }
}

function healthTone(h: "ok" | "warning" | "error"): string {
  return {
    ok: "bg-accent-green/15 text-accent-green",
    warning: "bg-accent-amber/15 text-accent-amber",
    error: "bg-accent-rose/15 text-accent-rose",
  }[h];
}

function relTime(iso: string, t: TFunction): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return t("admin.filemakerSync.relSeconds", { value: s });
  if (s < 3600) return t("admin.filemakerSync.relMinutes", { value: Math.round(s / 60) });
  if (s < 86400) return t("admin.filemakerSync.relHours", { value: Math.round(s / 3600) });
  return t("admin.filemakerSync.relDays", { value: Math.round(s / 86400) });
}

// ============================================================================
// Header
// ============================================================================

export function FilemakerSyncHeader() {
  const { t } = useTranslation();
  const state = useSyncState();
  const counts = useMemo(() => getAnomalyCounts(state), [state]);
  const totalOpen = counts.low + counts.medium + counts.high + counts.critical;
  return (
    <div className="rounded-lg border border-border bg-bg-panel p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Database
              className="h-5 w-5 text-benchmark-teal-bright"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-510 tracking-tight text-fg-primary">
              {t("admin.filemakerSync.title")}
            </h1>
            <Badge variant="teal" size="sm">v{state.serverVersion}</Badge>
          </div>
          <p className="mt-1 text-sm text-fg-tertiary">
            {t("admin.filemakerSync.intro")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-fg-quaternary">
            {t("admin.filemakerSync.agentHeartbeat")}
          </span>
          <Badge variant="default" size="sm">
            {relTime(state.agentHeartbeatAt, t)}
          </Badge>
          <span className="text-[10px] uppercase tracking-wider text-fg-quaternary">
            {t("admin.filemakerSync.queueDepth")}
          </span>
          <Badge variant="default" size="sm">
            {state.queueDepth} {t("admin.filemakerSync.events")}
          </Badge>
          {totalOpen > 0 ? (
            <Badge variant="danger" size="sm">
              {totalOpen} {totalOpen === 1 ? t("admin.filemakerSync.openAnomalyOne") : t("admin.filemakerSync.openAnomalyOther")}
            </Badge>
          ) : (
            <Badge variant="success" size="sm">
              {t("admin.filemakerSync.clean")}
            </Badge>
          )}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <AnomalyCountTile severity="critical" count={counts.critical} />
        <AnomalyCountTile severity="high" count={counts.high} />
        <AnomalyCountTile severity="medium" count={counts.medium} />
        <AnomalyCountTile severity="low" count={counts.low} />
      </div>
    </div>
  );
}

function AnomalyCountTile({
  severity,
  count,
}: {
  severity: AnomalySeverity;
  count: number;
}) {
  const { t } = useTranslation();
  const severityLabel: Record<AnomalySeverity, string> = {
    low: t("admin.filemakerSync.severityLow"),
    medium: t("admin.filemakerSync.severityMedium"),
    high: t("admin.filemakerSync.severityHigh"),
    critical: t("admin.filemakerSync.severityCritical"),
  };
  return (
    <div
      className={cn(
        "rounded-md p-3 text-center",
        ANOMALY_SEVERITY_TONE[severity],
      )}
    >
      <div className="text-2xl font-510 leading-none">{count}</div>
      <div className="mt-1 text-[10px] uppercase tracking-wider">
        {severityLabel[severity]}
      </div>
    </div>
  );
}

// ============================================================================
// Layout grid
// ============================================================================

export function FilemakerSyncLayoutsGrid() {
  const { t } = useTranslation();
  const state = useSyncState();
  // Group layouts by their Bench.jpg group for a stable display order.
  const groups: Array<{ group: string; labelKey: string; keys: FilemakerLayoutKey[] }> = [
    { group: "Master data", labelKey: "admin.filemakerSync.groupMasterData", keys: ["customers", "products"] },
    { group: "Selling", labelKey: "admin.filemakerSync.groupSelling", keys: ["sales-orders", "quotations", "invoices", "credit-notes"] },
    { group: "Suppliers + banking", labelKey: "admin.filemakerSync.groupSuppliers", keys: ["producers", "banksters", "lendings"] },
    { group: "Operations", labelKey: "admin.filemakerSync.groupOperations", keys: ["repairs"] },
    { group: "Reporting", labelKey: "admin.filemakerSync.groupReporting", keys: ["reports"] },
  ];
  return (
    <div className="space-y-5">
      {groups.map((g) => (
        <section key={g.group}>
          <h2 className="mb-2 text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
            {t(g.labelKey)}
          </h2>
          <div className="grid gap-3 lg:grid-cols-2">
            {g.keys.map((k) => {
              const cfg = FILEMAKER_LAYOUT_BY_KEY[k];
              const status = state.layouts[k];
              return (
                <FilemakerLayoutCard key={k} cfg={cfg} status={status} />
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function FilemakerLayoutCard({
  cfg,
  status,
}: {
  cfg: (typeof FILEMAKER_LAYOUTS)[number];
  status: ReturnType<typeof useSyncState>["layouts"][FilemakerLayoutKey];
}) {
  const { t } = useTranslation();
  const dir = directionLabel(cfg.direction);
  const DirIcon = dir.Icon;
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-510 text-fg-primary">
                {t(`admin.filemakerSync.layouts.${cfg.key}.label`)}
              </span>
              <Badge variant="default" size="sm" className="font-mono">
                {cfg.fmLayoutName}
              </Badge>
            </div>
            <p className="mt-0.5 text-[11px] text-fg-tertiary">
              {t(`admin.filemakerSync.layouts.${cfg.key}.description`)}
            </p>
          </div>
          <span
            className={cn(
              "inline-flex shrink-0 items-center gap-1 rounded px-2 py-0.5 text-[10px] font-510 uppercase tracking-wider",
              dir.tone,
            )}
          >
            <DirIcon className="h-3 w-3" strokeWidth={2} />
            {t(dir.labelKey)}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2 border-t border-border pt-3 text-xs">
          <Stat label={t("admin.filemakerSync.lastSync")} value={relTime(status.lastSyncedAt, t)} />
          <Stat
            label={t("admin.filemakerSync.lastDelta")}
            value={status.lastRunDelta === 0 ? "—" : `+${status.lastRunDelta}`}
          />
          <Stat label={t("admin.filemakerSync.totalRows")} value={String(status.totalRows)} />
        </div>
        <div className="flex items-center justify-between border-t border-border pt-3">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-510 uppercase tracking-wider",
                healthTone(status.health),
              )}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              {t(HEALTH_LABEL_KEY[status.health])}
            </span>
            <span className="text-[11px] text-fg-tertiary">
              {status.healthDetail ?? t("admin.filemakerSync.noOpenAnomalies")}
            </span>
          </div>
          <span className="text-[11px] text-fg-quaternary">
            {status.openAnomalies.length} {t("admin.filemakerSync.openCount")}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-fg-quaternary">
        {label}
      </div>
      <div className="text-fg-secondary">{value}</div>
    </div>
  );
}

// ============================================================================
// Anomaly list
// ============================================================================

export function FilemakerSyncAnomalies() {
  const { t } = useTranslation();
  const state = useSyncState();
  const anomalies = useMemo(() => getOpenAnomalies(state), [state]);
  // v1: mark-reviewed UI is local-only. Wiring to the backend
  // happens when fmmirror-mcp lands its first API in wp11g.
  const [reviewedIds, setReviewedIds] = useState<Set<string>>(
    () => new Set<string>(),
  );
  function markReviewed(id: string) {
    setReviewedIds((s) => {
      const next = new Set(s);
      next.add(id);
      return next;
    });
  }
  const visible = anomalies.filter((a) => !reviewedIds.has(a.id));
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle
              className="h-4 w-4 text-accent-rose"
              strokeWidth={1.5}
            />
            {t("admin.filemakerSync.openAnomalies")}
          </CardTitle>
          <CardDescription>
            {t("admin.filemakerSync.anomaliesDescription")}
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" disabled>
          <RefreshCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
          {t("admin.filemakerSync.rerunSync")}
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {visible.length === 0 ? (
          <p className="px-5 py-4 text-xs text-fg-quaternary">
            {t("admin.filemakerSync.noAnomalies")}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {visible.map((a) => {
              const layout = FILEMAKER_LAYOUT_BY_KEY[a.layoutKey];
              return (
                <li key={a.id} className="space-y-2 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-fg-quaternary">
                        {a.id}
                      </span>
                      <Badge
                        variant="default"
                        size="sm"
                        className={cn(ANOMALY_SEVERITY_TONE[a.severity])}
                      >
                        {t(SEVERITY_LABEL_KEY[a.severity])}
                      </Badge>
                      <Badge variant="default" size="sm">
                        {t(`admin.filemakerSync.anomalyKinds.${a.kind}`)}
                      </Badge>
                      <Link
                        to={`/admin/filemaker-sync/${a.layoutKey}`}
                        className="text-xs text-fg-secondary underline decoration-fg-quaternary underline-offset-2 hover:decoration-benchmark-teal hover:text-benchmark-teal-bright"
                      >
                        {t(`admin.filemakerSync.layouts.${layout.key}.label`)}
                      </Link>
                      <span className="font-mono text-[10px] text-fg-quaternary">
                        {a.sourcePk}
                      </span>
                    </div>
                    <span className="text-[11px] text-fg-tertiary">
                      {relTime(a.detectedAt, t)}
                    </span>
                  </div>
                  <div className="text-sm text-fg-primary">{a.summary}</div>
                  {a.detail && (
                    <p className="text-xs text-fg-tertiary">{a.detail}</p>
                  )}
                  {a.suggestedFix && (
                    <div className="flex items-start gap-2 rounded-md border border-benchmark-teal/30 bg-benchmark-teal/5 p-2">
                      <Sparkles
                        className="mt-0.5 h-3.5 w-3.5 text-benchmark-teal-bright"
                        strokeWidth={1.5}
                      />
                      <p className="text-xs text-fg-secondary">
                        <span className="text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
                          {t("admin.filemakerSync.suggestedFix")}
                        </span>{" "}
                        {a.suggestedFix}
                      </p>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => markReviewed(a.id)}
                    >
                      <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
                      {t("admin.filemakerSync.markReviewed")}
                    </Button>
                    <Button size="sm" variant="ghost">
                      <ExternalLink
                        className="h-3.5 w-3.5"
                        strokeWidth={1.5}
                      />
                      {t("admin.filemakerSync.openInFilemaker")}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Audit
// ============================================================================

export function FilemakerSyncAudit() {
  const { t } = useTranslation();
  const state = useSyncState();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield
            className="h-4 w-4 text-benchmark-teal-bright"
            strokeWidth={1.5}
          />
          {t("admin.filemakerSync.auditTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-y divide-border">
          {state.audit.map((a) => (
            <li key={a.id} className="flex items-start gap-3 px-5 py-3">
              <div className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full bg-bg-elevated text-fg-tertiary">
                <Cloud className="h-3 w-3" strokeWidth={2} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-fg-quaternary">
                    {a.id}
                  </span>
                  <Badge variant="default" size="sm">
                    {a.action}
                  </Badge>
                  <span className="font-mono text-[10px] text-fg-quaternary">
                    {t("admin.filemakerSync.by")} {a.actor}
                  </span>
                  <span className="ml-auto text-[11px] text-fg-tertiary">
                    {relTime(a.ts, t)}
                  </span>
                </div>
                <p className="mt-1 text-sm text-fg-secondary">{a.summary}</p>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Composite page (mounted at /admin/filemaker-sync)
// ============================================================================

export function FilemakerSyncPage() {
  return (
    <div className="space-y-5">
      <FilemakerSyncHeader />
      <FilemakerSyncLayoutsGrid />
      <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
        <FilemakerSyncAnomalies />
        <FilemakerSyncAudit />
      </div>
      <FilemakerSyncTaskLog />
    </div>
  );
}

// ============================================================================
// Historical task log (per-agent runs)
// ============================================================================

function fmtDuration(ms: number | null, t: TFunction): string {
  if (ms === null) return t("admin.filemakerSync.durationRunning");
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

export function FilemakerSyncTaskLog() {
  const { t } = useTranslation();
  // Filter state
  const [agentFilter, setAgentFilter] = useState<"all" | SyncTaskLog["agent"]>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | SyncTaskStatus>("all");

  const visible = useMemo(() => {
    return [...SEED_TASK_LOGS]
      .filter((t) => agentFilter === "all" || t.agent === agentFilter)
      .filter((t) => statusFilter === "all" || t.status === statusFilter)
      .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  }, [agentFilter, statusFilter]);

  const counts = useMemo(() => {
    const c = { ok: 0, warning: 0, error: 0, running: 0 };
    for (const t of SEED_TASK_LOGS) c[t.status] += 1;
    return c;
  }, []);

  // Distinct agent keys for the filter buttons.
  const agentKeys = Array.from(new Set(SEED_TASK_LOGS.map((t) => t.agent)));

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <RefreshCcw
              className="h-4 w-4 text-benchmark-teal-bright"
              strokeWidth={1.5}
            />
            {t("admin.filemakerSync.taskLogTitle")}
          </CardTitle>
          <CardDescription>
            {t("admin.filemakerSync.taskLogDescription")}{" "}
            <code>{t("admin.filemakerSync.syncSettingsPath")}</code>.
          </CardDescription>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant="success" size="sm">{counts.ok} {t("admin.filemakerSync.ok")}</Badge>
          <Badge variant="warning" size="sm">{counts.warning} {t("admin.filemakerSync.warning")}</Badge>
          <Badge variant="danger" size="sm">{counts.error} {t("admin.filemakerSync.error")}</Badge>
          {counts.running > 0 && (
            <Badge variant="default" size="sm">{counts.running} {t("admin.filemakerSync.running")}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-fg-tertiary">{t("admin.filemakerSync.agentLabel")}</span>
            <button
              type="button"
              onClick={() => setAgentFilter("all")}
              aria-pressed={agentFilter === "all"}
              className={cn(
                "rounded px-2 py-1 text-[10px] font-510 uppercase tracking-wider transition-colors",
                agentFilter === "all"
                  ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                  : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary",
              )}
            >
              {t("admin.filemakerSync.all")}
            </button>
            {agentKeys.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setAgentFilter(a)}
                aria-pressed={agentFilter === a}
                className={cn(
                  "rounded px-2 py-1 text-[10px] font-510 uppercase tracking-wider transition-colors",
                  agentFilter === a
                    ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                    : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary",
                )}
              >
                {a}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-fg-tertiary">{t("admin.filemakerSync.statusLabel")}</span>
            {(["all", "ok", "warning", "error", "running"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                aria-pressed={statusFilter === s}
                className={cn(
                  "rounded px-2 py-1 text-[10px] font-510 uppercase tracking-wider transition-colors",
                  statusFilter === s
                    ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                    : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary",
                )}
              >
                {s === "all" ? t("admin.filemakerSync.all") : t(TASK_STATUS_LABEL_KEY[s])}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {visible.length === 0 ? (
          <p className="text-xs text-fg-quaternary">{t("admin.filemakerSync.noRuns")}</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-bg-canvas/40 text-fg-quaternary">
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">{t("admin.filemakerSync.started")}</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">{t("admin.filemakerSync.agent")}</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">{t("admin.filemakerSync.direction")}</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">{t("admin.filemakerSync.target")}</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider">{t("admin.filemakerSync.rows")}</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider">{t("admin.filemakerSync.duration")}</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">{t("admin.filemakerSync.status")}</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">{t("admin.filemakerSync.triggeredBy")}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((task) => (
                  <tr key={task.id} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2 font-mono text-fg-secondary">
                      {new Date(task.startedAt).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 font-mono text-fg-secondary">{t(`admin.filemakerSync.agents.${task.agent}`)}</td>
                    <td className="px-3 py-2">
                      <Badge variant="default" size="sm">{t(`admin.filemakerSync.directions.${task.direction}`)}</Badge>
                    </td>
                    <td className="px-3 py-2 font-mono text-fg-secondary">{task.target}</td>
                    <td className="px-3 py-2 text-right font-mono">{task.rowsAffected}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtDuration(task.durationMs, t)}</td>
                    <td className="px-3 py-2">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-510 uppercase tracking-wider",
                          SYNC_STATUS_TONE[task.status],
                        )}
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        {t(TASK_STATUS_LABEL_KEY[task.status])}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-fg-tertiary">{task.triggeredBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Errors rollup */}
        {visible.some((task) => task.errors.length > 0) && (
          <div className="space-y-2">
            <h4 className="text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
              {t("admin.filemakerSync.errorsInView")}
            </h4>
            <ul className="space-y-1">
              {visible
                .filter((task) => task.errors.length > 0)
                .flatMap((task) =>
                  task.errors.map((e, i) => (
                    <li
                      key={`${task.id}-${i}`}
                      className="flex items-start gap-2 rounded-md border border-accent-rose/30 bg-accent-rose/5 p-2 text-xs"
                    >
                      <span className="font-mono text-[10px] text-accent-rose">{e.code}</span>
                      <span className="flex-1 text-fg-secondary">{e.message}</span>
                      <span className="font-mono text-[10px] text-fg-quaternary">
                        {task.id}
                      </span>
                    </li>
                  )),
                )}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Layout detail page
// ============================================================================

export function FilemakerSyncLayoutDetail() {
  // v1 keeps this lightweight: a per-layout summary plus the
  // anomalies scoped to that layout. Full per-row diff lands
  // in wp11c once the backend is live.
  const { t } = useTranslation();
  const state = useSyncState();
  const { layoutKey } = useParams<{ layoutKey: string }>();
  const key = (layoutKey ?? "") as FilemakerLayoutKey;
  if (!key || !(key in state.layouts)) {
    return (
      <div className="space-y-4">
        <Link
          to="/admin/filemaker-sync"
          className="inline-flex items-center gap-1 text-sm text-fg-tertiary hover:text-fg-primary"
        >
          <ChevronRight
            className="h-3 w-3 rotate-180"
            strokeWidth={1.5}
          />
          {t("admin.filemakerSync.backToSync")}
        </Link>
        <p className="text-sm text-fg-quaternary">
          {t("admin.filemakerSync.layoutNotFound")}
        </p>
      </div>
    );
  }
  const cfg = FILEMAKER_LAYOUT_BY_KEY[key];
  const status = state.layouts[key];
  const dir = directionLabel(cfg.direction);
  const DirIcon = dir.Icon;
  return (
    <div className="space-y-5">
      <Link
        to="/admin/filemaker-sync"
        className="inline-flex items-center gap-1 text-sm text-fg-tertiary hover:text-fg-primary"
      >
        <ChevronRight className="h-3 w-3 rotate-180" strokeWidth={1.5} />
        {t("admin.filemakerSync.backToSync")}
      </Link>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-510 tracking-tight text-fg-primary">
              {t(`admin.filemakerSync.layouts.${cfg.key}.label`)}
            </h1>
            <Badge variant="default" size="sm" className="font-mono">
              {cfg.fmLayoutName}
            </Badge>
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-510 uppercase tracking-wider",
                dir.tone,
              )}
            >
              <DirIcon className="h-3 w-3" strokeWidth={2} />
              {t(dir.labelKey)}
            </span>
          </div>
          <p className="mt-1 text-sm text-fg-tertiary">{t(`admin.filemakerSync.layouts.${cfg.key}.description`)}</p>
        </div>
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-510 uppercase tracking-wider",
            healthTone(status.health),
          )}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {t(HEALTH_LABEL_KEY[status.health])}
        </span>
      </header>
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label={t("admin.filemakerSync.lastSync")} value={relTime(status.lastSyncedAt, t)} />
        <Stat
          label={t("admin.filemakerSync.lastDelta")}
          value={status.lastRunDelta === 0 ? "—" : `+${status.lastRunDelta}`}
        />
        <Stat label={t("admin.filemakerSync.totalRows")} value={String(status.totalRows)} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{t("admin.filemakerSync.openAnomalies")}</CardTitle>
          <CardDescription>
            {t("admin.filemakerSync.layoutAnomaliesDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {status.openAnomalies.length === 0 ? (
            <p className="px-5 py-4 text-xs text-fg-quaternary">
              {t("admin.filemakerSync.noLayoutAnomalies")}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {status.openAnomalies.map((a) => (
                <li key={a.id} className="space-y-2 p-5">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-fg-quaternary">
                      {a.id}
                    </span>
                    <Badge
                      variant="default"
                      size="sm"
                      className={cn(ANOMALY_SEVERITY_TONE[a.severity])}
                    >
                      {t(SEVERITY_LABEL_KEY[a.severity])}
                    </Badge>
                    <Badge variant="default" size="sm">
                      {t(`admin.filemakerSync.anomalyKinds.${a.kind}`)}
                    </Badge>
                  </div>
                  <div className="text-sm text-fg-primary">{a.summary}</div>
                  {a.detail && (
                    <p className="text-xs text-fg-tertiary">{a.detail}</p>
                  )}
                  {a.suggestedFix && (
                    <div className="flex items-start gap-2 rounded-md border border-benchmark-teal/30 bg-benchmark-teal/5 p-2">
                      <Sparkles
                        className="mt-0.5 h-3.5 w-3.5 text-benchmark-teal-bright"
                        strokeWidth={1.5}
                      />
                      <p className="text-xs text-fg-secondary">
                        <span className="text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
                          {t("admin.filemakerSync.suggestedFix")}
                        </span>{" "}
                        {a.suggestedFix}
                      </p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// Dashboard widget (also mounted on /admin)
// ============================================================================

export function FilemakerSyncDashboardWidget() {
  const { t } = useTranslation();
  const state = useSyncState();
  const counts = useMemo(() => getAnomalyCounts(state), [state]);
  const totalOpen = counts.low + counts.medium + counts.high + counts.critical;
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Database
              className="h-4 w-4 text-benchmark-teal-bright"
              strokeWidth={1.5}
            />
            {t("admin.filemakerSync.widgetTitle")}
          </CardTitle>
          <CardDescription>
            {t("admin.filemakerSync.widgetMeta")}{state.serverVersion}{" "}
            {t("admin.filemakerSync.widgetHeartbeat")}{" "}
            {relTime(state.agentHeartbeatAt, t)}
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" href="/admin/filemaker-sync">
          {t("admin.filemakerSync.openSync")}
          <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <AnomalyCountTile severity="critical" count={counts.critical} />
          <AnomalyCountTile severity="high" count={counts.high} />
          <AnomalyCountTile severity="medium" count={counts.medium} />
          <AnomalyCountTile severity="low" count={counts.low} />
        </div>
        <p className="text-[11px] text-fg-tertiary">
          {totalOpen === 0
            ? `${t("admin.filemakerSync.cleanSummary")} ${relTime(state.agentHeartbeatAt, t)}`
            : `${totalOpen} ${totalOpen === 1 ? t("admin.filemakerSync.openAnomalyOne") : t("admin.filemakerSync.openAnomalyOther")} ${t("admin.filemakerSync.across")} ${
                Object.values(state.layouts).filter((l) => l.openAnomalies.length > 0)
                  .length
              } ${
                Object.values(state.layouts).filter((l) => l.openAnomalies.length > 0)
                  .length === 1
                  ? t("admin.filemakerSync.layoutOne")
                  : t("admin.filemakerSync.layoutOther")
              }. ${t("admin.filemakerSync.triageOnSyncPage")}`}
        </p>
      </CardContent>
    </Card>
  );
}
